/**
 * Vue Router 4 — history mode.
 * Routes with meta.public are reachable without a token; all others require
 * getters.authenticated or redirect to /login. There is no catch-all 404.
 */
import { createRouter, createWebHistory } from 'vue-router';
import store from '@/store';
import { loadPlatformConfig } from '@/helpers/platformConfig';

const routes = [
  {
    path: '/signup',
    name: 'Sign Up',
    component: () => import('@/views/public/SignUpView.vue'),
    meta: {
      public: true,
      account: false,
      owner: false,
      title: 'Sign Up',
    },
  },
  {
    path: '/signup/company',
    name: 'Company Sign Up',
    component: () => import('@/views/public/CompanySignUpView.vue'),
    meta: {
      public: true,
      title: 'Create Account',
    },
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/public/LoginView.vue'),
    meta: {
      public: true,
      account: false,
      owner: false,
      title: 'Login',
    },
  },
  {
    path: '/forgot-password',
    name: 'Forgot Password',
    component: () => import('@/views/public/ForgotPasswordView.vue'),
    meta: {
      public: true,
      account: false,
      owner: false,
      title: 'Forgot Password',
    },
  },
  {
    path: '/reset-password',
    name: 'Reset Password',
    component: () => import('@/views/public/ResetPasswordView.vue'),
    meta: {
      public: true,
      account: false,
      owner: false,
      title: 'Reset Password',
    },
  },
  {
    path: '/verify-email',
    name: 'Verify Email',
    component: () => import('@/views/public/VerifyEmailView.vue'),
    meta: {
      public: true,
      owner: false,
      title: 'Verify Email',
    },
  },
  {
    path: '/',
    name: 'Dashboard',
    component: () => import('@/views/private/DashboardView.vue'),
    meta: {
      public: false,
      title: 'Dashboard',
    },
  },
  {
    path: '/clicks',
    name: 'Clicks',
    component: () => import('@/views/private/ClicksView.vue'),
    meta: {
      public: false,
      title: 'Clicks',
    },
  },
  {
    path: '/leads',
    name: 'Leads',
    component: () => import('@/views/private/LeadsView.vue'),
    meta: {
      public: false,
      title: 'Leads',
    },
  },
  {
    path: '/commissions',
    name: 'Commissions',
    component: () => import('@/views/private/CommissionsView.vue'),
    meta: {
      public: false,
      title: 'Commissions',
    },
  },
  {
    path: '/payouts',
    name: 'Payouts',
    component: () => import('@/views/private/PayoutsView.vue'),
    meta: {
      public: false,
      title: 'Payouts',
    },
  },
  {
    path: '/settings',
    name: 'Settings',
    component: () => import('@/views/private/SettingsView.vue'),
    meta: {
      public: false,
      title: 'Settings',
    },
  },
  {
    path: '/admin',
    name: 'Admin',
    component: () => import('@/views/admin/AdminOverviewView.vue'),
    meta: {
      public: false,
      admin: true,
      title: 'Admin',
    },
  },
  {
    path: '/admin/promoters',
    name: 'Admin Promoters',
    component: () => import('@/views/admin/AdminPromotersView.vue'),
    meta: {
      public: false,
      admin: true,
      title: 'Admin Promoters',
    },
  },
  {
    path: '/admin/campaigns',
    name: 'Admin Campaigns',
    component: () => import('@/views/admin/AdminCampaignsView.vue'),
    meta: {
      public: false,
      admin: true,
      title: 'Admin Campaigns',
    },
  },
  {
    path: '/admin/promotions',
    name: 'Admin Promotions',
    component: () => import('@/views/admin/AdminPromotionsView.vue'),
    meta: {
      public: false,
      admin: true,
      title: 'Admin Promotions',
    },
  },
  {
    path: '/admin/clicks',
    name: 'Admin Clicks',
    component: () => import('@/views/admin/AdminClicksView.vue'),
    meta: {
      public: false,
      admin: true,
      title: 'Admin Clicks',
    },
  },
  {
    path: '/admin/leads',
    name: 'Admin Leads',
    component: () => import('@/views/admin/AdminLeadsView.vue'),
    meta: {
      public: false,
      admin: true,
      title: 'Admin Leads',
    },
  },
  {
    path: '/admin/leads/:id',
    name: 'Admin Lead Detail',
    component: () => import('@/views/admin/AdminLeadDetailView.vue'),
    meta: {
      public: false,
      admin: true,
      title: 'Lead Detail',
    },
  },
  {
    path: '/admin/commissions',
    name: 'Admin Commissions',
    component: () => import('@/views/admin/AdminCommissionsView.vue'),
    meta: {
      public: false,
      admin: true,
      title: 'Admin Commissions',
    },
  },
  {
    path: '/admin/commissions/:id',
    name: 'Admin Commission Detail',
    component: () => import('@/views/admin/AdminCommissionDetailView.vue'),
    meta: {
      public: false,
      admin: true,
      title: 'Commission Detail',
    },
  },
  {
    path: '/admin/payouts',
    name: 'Admin Payouts',
    component: () => import('@/views/admin/AdminPayoutsView.vue'),
    meta: {
      public: false,
      admin: true,
      title: 'Admin Payouts',
    },
  },
  {
    path: '/admin/account',
    name: 'Admin Account',
    component: () => import('@/views/admin/AdminAccountSettingsView.vue'),
    meta: {
      public: false,
      admin: true,
      title: 'Account Settings',
    },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach(async (to, from, next) => {
  if (to.path === '/signup/company') {
    const config = await loadPlatformConfig();
    if (!config.platform_signup_enabled) {
      next('/signup');
      return;
    }
  }

  if (to.meta.public) {
    next();
  } else if (!store.getters.authenticated) {
    next('/login');
  } else if (to.meta.admin && !store.getters.isAdmin) {
    next('/');
  } else {
    next();
  }
});

router.afterEach((to) => {
  document.title = to.meta.title
    ? `${to.meta.title} | Webwalker Affiliate`
    : 'Webwalker Affiliate';
});

export default router;
